import { defineStyle, SystemStyleObject } from "@chakra-ui/react";

export const sharedFieldStyles = defineStyle({
  h: "40px",
  border: "none",
  rounded: "xl",
  fontWeight: "normal",
  bg: "transparent",
  outline: "none",
  color: "themeColor.form.fg",
  _disabled: {
    bg: "themeColor.bg.muted",
    opacity: 0.6,
    cursor: "not-allowed",
  },
});

const formFieldTransition = {
  transitionProperty: "border-color, box-shadow, background",
  transitionDuration: "200ms",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
} as const;

const formFieldInvalidStyles: SystemStyleObject = {
  borderColor: "themeColor.form.borderError",
  bg: "themeColor.form.bgError",
  _hover: {
    borderColor: "themeColor.form.borderError",
    boxShadow: "themeColor.form.errorFocus",
    bg: "themeColor.form.bgError",
  },
  _focusWithin: {
    borderColor: "themeColor.form.borderError",
    boxShadow: "themeColor.form.errorFocus",
    bg: "themeColor.form.bgError",
  },
};

const formFieldInteractiveStyles: SystemStyleObject = {
  _hover: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgHover",
  },
  _focusWithin: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgHover",
  },
};

export const formFieldFloatingWrapperStyles: SystemStyleObject = {
  border: "1px solid",
  borderColor: "transparent",
  rounded: "xl",
  bg: "themeColor.form.bg",
  ...formFieldTransition,
  ...formFieldInteractiveStyles,
  _invalid: formFieldInvalidStyles,
};

export const formFieldOutlinedWrapperStyles: SystemStyleObject = {
  border: "1px solid",
  borderColor: "themeColor.form.border",
  rounded: "xl",
  bg: "themeColor.form.bgSolid",
  ...formFieldTransition,
  _hover: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgHover",
  },
  _focusWithin: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgHover",
  },
  _invalid: formFieldInvalidStyles,
};

export const formFieldOutlinedInputWrapperStyles: SystemStyleObject = {
  ...formFieldOutlinedWrapperStyles,
  _hover: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgSolid",
  },
  _focusWithin: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
    bg: "themeColor.form.bgSolid",
  },
};

export const formSelectTriggerStyles = defineStyle({
  border: "1px solid",
  borderColor: "themeColor.form.border",
  bg: "transparent",
  color: "themeColor.form.fg",
  fontWeight: "normal",
  _hover: {
    borderColor: "themeColor.form.borderFocus",
  },
  _focusVisible: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
  },
});

export const formSelectContentStyles = defineStyle({
  bg: "themeColor.form.menuBg",
  border: "1px solid",
  borderColor: "themeColor.form.menuBorder",
});

export const formSelectValueColor = (hasValue: boolean) =>
  hasValue ? "themeColor.form.fg" : "themeColor.form.placeholder";

export const sharedWrapperStyles = defineStyle({
  border: "1px solid",
  borderColor: "themeColor.form.border",
  rounded: "xl",
  ...formFieldTransition,
  _hover: {
    borderColor: "themeColor.border.strong",
  },
  _focusWithin: {
    borderColor: "themeColor.form.borderFocus",
    boxShadow: "themeColor.form.focus",
  },
  "&[data-invalid]": {
    borderColor: "themeColor.form.borderError",
    bg: "themeColor.form.bgError",
    _focusWithin: {
      borderColor: "themeColor.form.borderError",
      boxShadow: "themeColor.form.errorFocus",
      bg: "themeColor.form.bgError",
    },
  },
});

export const sharedLabelFloatingStyles = defineStyle({
  pos: "absolute",
  bg: "transparent",
  px: "1",
  top: "1.5",
  insetStart: "3",
  fontWeight: "semibold",
  textStyle: "xs",
  pointerEvents: "none",
  transition: "position",
  color: "themeColor.form.label",
  py: "0.5",
  "&[data-float]": {
    top: "-1.5",
    insetStart: "2",
    color: "themeColor.form.labelActive",
    bg: "transparent",
    textTransform: "uppercase",
    textStyle: "2xs",
  },
});
